import { Money, ProductConditionCode } from "../../../common/models/CommonModels";
import { FileModel, Page, Quantity, QueryBase } from "../../../shared/models/models";
import { MultipleSelectFilterParameter } from "./customerModels";

export interface CustomerBuyerInventoryState {
  query: CustomerBuyerStockItemsQuery,
  stockItems?: Page<CustomerBuyerStockItem>;
  stockItem?: CustomerBuyerStockItem,
  groupedStockItems: Page<GroupedStockItem>,
  groupedStockItemsQuery?: GroupedStockItemQuery;
  filters: FiltersGroupedStockItems;
  filtersQuery: FiltersGroupedStockItemQuery;
  stockItemsUploadResult?: any,
  manufacturers: ManufacturerReference[],
  categories: CategoryReference[],
  loading: boolean,
  filterLoading: boolean;
  success?: string,
  error: any
}

export interface ChangeBuyerStockItemModel {
  categoryId: string;
  subcategoryId?: string;
  manufacturerId: string;
  manufacturerName: string;
  number: string;
  alternativeNumber?: string;
  conditionCode: ProductConditionCode;
  availabilityCode: ProductAvailabilityCode;
  quantity?: number;
  price?: Money;
  height?: number;
  width?: number;
  depth?: number;
  weight?: number;
  description?: string;
  file?: FileModel;
}

export interface ManufacturerReference {
  id: string;
  name: string;
}

export interface CategoryReference {
  id: string;
  name: string;
}

export interface ProductReference {
  number: string;
  manufacturer: ManufacturerReference;
  category: CategoryReference;
}

export interface SupplierReference {
  id: string;
  name: string;
}

export interface CustomerBuyerStockItem {
  id: string;
  alternativeNumber?: string;
  conditionCode: ProductConditionCode;
  availabilityCode: ProductAvailabilityCode;
  quantity?: Quantity;
  price?: Money;
  width?: number;
  height?: number;
  depth?: number;
  weight?: number;
  description?: string;
  product: ProductReference;
  supplier: SupplierReference;
  file: FileModel | null;
}

export interface GroupedStockItem {
  id: string;
  number: string;
  alternativeNumber?: string;
  manufacturerName: string;
  categoryName: string;
  suppliersCount: string;
  minPrice?: number;
  maxPrice?: number;
  quantity: Quantity;
  productConditions: ProductConditionCode[];
  countryIds: number[];
  images: FileModel[] | null;
}

export interface GroupedStockItemQuery {
  number?: string;
  manufacturerIds?: string[];
  conditionCodes?: number[];
  categoryIds?: string[];
  countryIds?: number[];
  groupIds?: string[];
  pageNo?: number;
  pageSize?: number;
}

export interface FiltersGroupedStockItemQuery {
  number?: string;
  manufacturerIds?: string[];
  categoryIds?: string[];
  conditionCodes?: number[];
  countryIds?: number[];
  groupIds?: string[];
}

export interface FiltersGroupedStockItems {
  countries: MultipleSelectFilterParameter[];
  manufacturers: MultipleSelectFilterParameter[];
  categories: MultipleSelectFilterParameter[];
  conditions: MultipleSelectFilterParameter[];
  groups: MultipleSelectFilterParameter[];
}

export interface FilterGroupedStockItems {
  id: number | string;
  isEnabled: boolean;
  name: string;
  value: number;
}

export interface CustomerBuyerStockItemsQuery extends QueryBase<CustomerBuyerStockItemsQueryParameter> {
  supplierId?: string;
  manufacturerId?: string;
  categoryId?: string;
  availabilityCode?: ProductAvailabilityCode;
  conditionCodes?: ProductConditionCode[];
  number?: string;
  manufacturerName?: string;
}

export enum CustomerBuyerStockItemsQueryParameter {
  Name = 1,
  Number = 2,
  ManufacturerName = 3,
  Condition = 4,
  Availability = 5,
  Price = 6,
  Quantity = 7,
  Weight = 8,
  SupplierName = 9
}

export enum ProductAvailabilityCode {
  inStock = 1,
  in7Days = 2,
  in14Days = 3,
  in1Month = 4
}

export enum DeliveryCode {
  sameDay = 1,
  nextDay = 2,
  thisWeek = 3
}

export interface StockItemSupplier {
  id: string;
  name: string;
  countryId: number;
  distance?: number;
  minPrice?: number;
  maxPrice: number;
  quantity: Quantity;
  logo?: FileModel;
  availabilityCode?: ProductAvailabilityCode;
  conditionCodes: ProductConditionCode[];
}

export interface StockItemSupplierDetails {
  id: string;
  name: string;
  number: string;
  alternativeNumber: string;
  manufacturerName: string;
  categoryName: string;
  supplyVariants: SupplyVariant[];
}

export interface SupplyVariant {
  id: string;
  warrantyPeriodMonths?: number;
  quantity: Quantity;
  availabilityCode: ProductAvailabilityCode;
  price: number;
  image: FileModel | undefined;
  description: string;
  conditionCode: ProductConditionCode;
}

export interface StockItemSuppliersQuery extends QueryBase<StockItemSuppliersQueryParameter> {
  minPrice?: number;
  maxPrice: number;
  countryIds: number[],
  deliveryCodes: number[],
  conditionCodes: number[],
  groupIds: string[]
}

export enum StockItemSuppliersQueryParameter {
  name = 1,
  price = 2
}

class ProductAvailabilities {
  private readonly map = {} as any;

  constructor() {
    this.map[ProductAvailabilityCode.inStock] = "In stock";
    this.map[ProductAvailabilityCode.in7Days] = "In 7 days";
    this.map[ProductAvailabilityCode.in14Days] = "In 14 days";
    this.map[ProductAvailabilityCode.in1Month] = "In 1 month";
  }

  productAvailability = (code: ProductAvailabilityCode) => {
    return this.map[code] || '';
  };
}

export const { productAvailability } = new ProductAvailabilities();

class DeliveryCodes {
  private readonly map = {} as any;

  constructor() {
    this.map[DeliveryCode.sameDay] = "Same day";
    this.map[DeliveryCode.nextDay] = "Next day";
    this.map[DeliveryCode.thisWeek] = "This week";
  }

  deliveryCode = (code: DeliveryCode) => {
    return this.map[code] || '';
  };
}

export const { deliveryCode } = new DeliveryCodes();
